import { MasterDataType } from "domain/entity/MasterData/MasterDataEntity";
import { CopyTariffProposalItemForRecalCriteria } from "domain/entity/ProposalCopyingForRecal/CopyTariffProposalItemForRecalCriteria";
import { ProposalCopyingForRecalEntity } from "domain/entity/ProposalCopyingForRecal/ProposalCopyingForRecalEntity";
import { ProposalCopyingForRecalItemEntity } from "domain/entity/ProposalCopyingForRecal/ProposalCopyingForRecalItemEntity";
import { EMPTY_PROPOSAL_COPY_FOR_RECAL_SEARCH_CRITERIA, ProposalCopyingForRecalSearchCriteria } from "domain/entity/ProposalCopyingForRecal/ProposalCopyingForRecalSearchCriteria";
import { EMPTY_STANDARD_PROPOSAL_ITEM_PREVIEW_ENTITY, StandardProposalItemPreviewEntity } from "domain/entity/StandardProposal/StandardProposalItemPreviewEntity";
import { EMPTY_STD_PROPOSAL_COPY_TO_ENTITY, StdProposalCopyToEntity } from "domain/entity/StandardProposal/StdProposalCopyToEntity";
import { EMPTY_STD_PROPOSAL_COPY_TO_NEW_PROPOSAL_ENTITY, StdProposalCopyToNewProposalEntity } from "domain/entity/StandardProposal/StdProposalCopyToNewProposalEntity";
import { EMPTY_STD_PROPOSAL_COPY_TO_PROPOSAL_ENTITY, StdProposalCopyToProposalEntity } from "domain/entity/StandardProposal/StdProposalCopyToProposalEntity";
import { EMPTY_STD_PROPOSAL_TARIFF_ITEM_TIER_ENTITY, StdProposalTariffItemTierEntity } from "domain/entity/StandardProposal/StdProposalTariffItemTierEntity";
import { CompanyRepository } from "domain/repository/Company/CompanyRepo";
import { ExchangeRateRepository } from "domain/repository/ExchangeRate/ExchangeRateRepo";
import { MasterDataRepository } from "domain/repository/MasterData/MasterDataRepo";
import { ProposalCopyingForRecalRepository } from "domain/repository/ProposalCopyingForRecal/ProposalCopyingForRecalRepo";
import { StandardProposalItemRepository } from "domain/repository/StandardProposal/StandardProposalItemRepo";
import { StandardProposalRepository } from "domain/repository/StandardProposal/StandardProposalRepo";
import _ from "lodash";
import { copyEditNewProposalNoValidationSchema, copyEditProposalNoValidationSchema, copyToEditPercentageValidationSchema, createTempSaveTariffItemTierValidationSchema } from "presentation/constant/StandardProposal/StandardProposalValidationSchema";
import { Validation } from "presentation/constant/Validation";
import { ProposalCopyingForRecalModel } from "presentation/model/ProposalCopyingForRecal/ProposalCopyingForRecalModel";
import { Dispatch, SetStateAction } from "react";
import { IFieldValue } from "veronica-ui-component/dist/component/core";
import BaseViewModel from "../BaseViewModel";

interface ProposalCopyingForRecalVMProps extends BaseViewModel {
    dispatch: [
        Dispatch<SetStateAction<ProposalCopyingForRecalModel>> | ((value: SetStateAction<ProposalCopyingForRecalModel>) => void),
    ],
    proposalCopyingForRecalRepo: ProposalCopyingForRecalRepository,
    companyRepo: CompanyRepository,
    masterDataRepo: MasterDataRepository,
    standardProposalRepo: StandardProposalRepository,
    standardProposalItemRepo: StandardProposalItemRepository,
    exchangeRateRepo: ExchangeRateRepository,
}

export const ProposalCopyingForRecalVM = ({ dispatch, proposalCopyingForRecalRepo, companyRepo, masterDataRepo,
    exchangeRateRepo }: ProposalCopyingForRecalVMProps) => {
    const [proposalCopyingForRecalDispatch] = dispatch;

    const loadDropdownOption = async () => {

        await exchangeRateRepo.getAllCurrencies().then(
            currencies => {
                let currencyDropdownOptions = currencies?.map((currency) => ({
                    dropdownLabel: currency.currencyCode,
                    tagLabel: currency.currencyCode,
                    value: currency.currencyCode,
                })) ?? []

                proposalCopyingForRecalDispatch(prevState => ({
                    ...prevState,
                    dynamicOptions: {
                        ...prevState.dynamicOptions,
                        currencyCodeDropdownOptions: currencyDropdownOptions,
                    }
                }))
            }
        )

        await companyRepo.getAllCompanyForCombobox().then(
            companies => {
                companies = companies?.filter(company => company.activeInd === 'Y');
                let companyCodeDropdownOption = companies?.map((company) => ({
                    dropdownLabel: company.companyCode,
                    tagLabel: company.companyCode,
                    value: company.companyCode,
                })) ?? []
                companyCodeDropdownOption = _.orderBy(companyCodeDropdownOption, "dropdownLabel");

                proposalCopyingForRecalDispatch(prevState => ({
                    ...prevState,
                    dynamicOptions: {
                        ...prevState.dynamicOptions,
                        companyCodeDropdownOptions: [
                            ...companyCodeDropdownOption],

                    }
                }))
            }
        );

        await masterDataRepo.getMasterDataByKey(MasterDataType.TERMINAL).then(
            operatingTmls => {
                const operatingTmlDropdownOptions = operatingTmls?.map((operatingTml) => ({
                    dropdownLabel: operatingTml.code,
                    tagLabel: operatingTml.code,
                    value: operatingTml.code,
                })) ?? []

                proposalCopyingForRecalDispatch(prevState => ({
                    ...prevState,
                    dynamicOptions: {
                        ...prevState.dynamicOptions,
                        operatingTmlDropdownOptions: operatingTmlDropdownOptions,
                    }
                }))
            }
        )

    }

    const searchProposalsForRecal = async (searchCriteria: ProposalCopyingForRecalSearchCriteria) => {
        proposalCopyingForRecalDispatch(prevState => {
            return {
                ...prevState,
                selectedRows: [],
                proposalCopyingForRecalList: [],
                searchCriteria: searchCriteria,
                isForRecal: false,
                searchStatus: '',
                isShowTariffItemPanel: false,
                isEditTier: false,
                selectedItemRows: [],
                isShowCopyToCompanyPanel: false,
                copyToNewProposalList: [],
                copyToPercentageList: [],
                copyToProposalList: [],
                companyCodeList: [],
                tariffItemList: [],
            }
        })
        await proposalCopyingForRecalRepo.getEntities(searchCriteria).then((data) => {
            if (data) {
                proposalCopyingForRecalDispatch(prevState => {
                    return {
                        ...prevState,
                        proposalCopyingForRecalList: data,
                    }
                })
            }
        }).catch((error) => {
            return [];
        })

    }

    const searchStandardProposalItem = async (entity: ProposalCopyingForRecalEntity) => {
        if (!entity.key) {
            return;
        }
        onShowLoading();
        await proposalCopyingForRecalRepo.getTariffItems(entity).then((data) => {
            if (data) {
                const newData = _.orderBy(data, ["priority"]);
                proposalCopyingForRecalDispatch(prevState => {
                    return {
                        ...prevState,
                        tariffItemList: newData,
                        currentSelectedRow: entity,
                        isShowTariffItemPanel: true,
                        isLoading: false,
                        isShowCriteriaPanel: false,
                        isEdit: false,
                        isShowEditPanel: false,
                    }
                })
            }
            onHideLoading();
        }).catch((error) => {
            onHideLoading();
            return [];
        })

    }

    const updateSelectedRows = async (rows: ProposalCopyingForRecalEntity[]) => {
        if (rows?.length === 1) {
            proposalCopyingForRecalDispatch(prevState => {
                return {
                    ...prevState,
                    tariffItemList: [],
                    selectedItemRows: [],
                }
            })
            searchStandardProposalItem(rows[0]);
        }
    }

    const updateSelectedTariffItemRows = async (rows: ProposalCopyingForRecalItemEntity[]) => {
        proposalCopyingForRecalDispatch(prevState => {
            return {
                ...prevState,
                selectedItemRows: rows,
            }
        })
    }

    const onTariffItemCloseClick = async () => {
        proposalCopyingForRecalDispatch(prevState => {
            return {
                ...prevState,
                isShowTariffItemPanel: false,
                companyCodeList: [],
                selectedItemRows: [],
                tariffItemList: [],
            }
        })
    }

    const onGroupButtonClick = async (buttonId: string) => {
        proposalCopyingForRecalDispatch(prevState => {
            return {
                ...prevState,
                searchStatus: buttonId,
                isShowTariffItemPanel: false,
                isEditTier: false,
                proposalCopyingForRecalList: [],
                selectedRows: [],
                selectedItemRows: [],
                isShowCopyToCompanyPanel: false,
                copyToNewProposalList: [],
                copyToPercentageList: [],
                copyToProposalList: [],
                companyCodeList: [],
                tariffItemList: [],
            }
        })
        let newSearchCriteria = null;
        newSearchCriteria = { ...EMPTY_PROPOSAL_COPY_FOR_RECAL_SEARCH_CRITERIA }
        await proposalCopyingForRecalRepo.searchRecalProposal(newSearchCriteria).then((data) => {
            if (data) {
                proposalCopyingForRecalDispatch(prevState => {
                    return {
                        ...prevState,
                        proposalCopyingForRecalList: data,
                        isForRecalCount: data.length,
                        isForRecal: true
                    }
                })
            }
        }).catch((error) => {
            return [];
        })
    }


    const onSearchCriteriaResetClick = async () => {
        proposalCopyingForRecalDispatch(prevState => {
            return {
                ...prevState,
                searchCriteria: { ...EMPTY_PROPOSAL_COPY_FOR_RECAL_SEARCH_CRITERIA },
            }
        })
    }

    const onSearchClick = async () => {
        proposalCopyingForRecalDispatch(prevState => {
            return {
                ...prevState,
                isShowCriteriaPanel: !prevState.isShowCriteriaPanel,
            }
        })
    }

    const onFieldChange = async (fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) => {
        let val: any = fieldValue;
        proposalCopyingForRecalDispatch(prevState => {
            return {
                ...prevState,
                searchCriteria: {
                    ...prevState.searchCriteria,
                    [fieldKey]: val,
                },
                masterState: {
                    ...prevState.masterState,
                    allFormState: {
                        ...prevState.masterState.allFormState,
                        [fieldKey]: ''
                    },
                }
            }
        })
    }

    const onCopyToFieldChange = async (isCopyToNewProposal: boolean, fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) => {
        let val: any = fieldValue;
        if (_.isArray(val)) {
            val = _.uniq(val?.map((item: any) => item.value || item.key));
        }
        if (!isCopyToNewProposal) {
            proposalCopyingForRecalDispatch(prevState => {
                let updatedCompanyCode: StdProposalCopyToProposalEntity[] = [];
                if (val) {
                    let copyToCompanyList = val as string[];
                    let items: StdProposalCopyToProposalEntity[] = prevState.copyToProposalList?.slice();
                    const filterCompanyCode = items.filter(item => copyToCompanyList.includes(item.companyCode));
                    let tariffItems = prevState.selectedItemRows?.slice();
                    updatedCompanyCode = copyToCompanyList.reduce((acc, companyCode) => {
                        const existingCode = acc?.find(item => item.companyCode === companyCode);
                        if (!existingCode) {
                            let TariffItemTierList = new Map<string, StdProposalTariffItemTierEntity[]>();
                            tariffItems.forEach(tariff => {
                                const tierList = tariff.tierList?.slice();
                                const copyToPercentages = prevState.copyToPercentageList.filter(item => item.tariffType === tariff.tariffType);
                                let copyToNewTariffItemTierList: StdProposalTariffItemTierEntity[] = [];
                                if (!_.isEmpty(copyToPercentages)) {
                                    const calPercentage = copyToPercentages[0];
                                    copyToNewTariffItemTierList = tierList ? tierList.map(tier => {
                                        const newRate = applyRounding(
                                            (tier?.rate ?? 0) * (calPercentage.chargePercentage * 0.01),
                                            calPercentage.roundMethod,
                                            calPercentage.decimalPlace
                                        );
                                        const newMinimum = applyRounding(
                                            (tier?.minimum ?? 0) * (calPercentage.chargePercentage * 0.01),
                                            calPercentage.roundMethod,
                                            calPercentage.decimalPlace
                                        );
                                        const newSurcharge = tier?.surcharge ? applyRounding(
                                            tier?.surcharge * (calPercentage.chargePercentage * 0.01),
                                            calPercentage.roundMethod,
                                            calPercentage.decimalPlace
                                        ) : null;
                                        const newAdditionalRate = tier?.additionalRate ? applyRounding(
                                            tier?.additionalRate * (calPercentage.chargePercentage * 0.01),
                                            calPercentage.roundMethod,
                                            calPercentage.decimalPlace
                                        ) : null;
                                        return {
                                            ...tier,
                                            rate: newRate,
                                            minimum: newMinimum,
                                            surcharge: newSurcharge,
                                            additionalRate: newAdditionalRate,
                                        }
                                    }) : [];
                                }
                                TariffItemTierList.set(tariff.tariffType + tariff.tariffCode, copyToNewTariffItemTierList);
                            });
                            // let previewEntities: StandardProposalItemPreviewEntity[] = [];
                            // const newCopyToCompanyList = newCompanyList.map(entity => entity.companyCode);
                            // previewEntities = copyToCompanyList.flatMap(companyCode =>
                            //     tariffItems.map(entity => ({
                            //         companyCode,
                            //         itemId: entity.key,
                            //         tariffType: entity.tariffType,
                            //         tariffCode: entity.tariffCode,
                            //         forworderCode: entity.forworderCode,
                            //         copyToNewTariffItemTierList: copyToNewTariffItemTierList
                            //     } as StandardProposalItemPreviewEntity))
                            // );
                            let previewEntities = new Map<string, StandardProposalItemPreviewEntity[]>();
                            copyToCompanyList.forEach(companyCode => {
                                const companyItems = tariffItems.map(entity => ({
                                    companyCode,
                                    itemId: entity.key,
                                    tariffType: entity.tariffType,
                                    tariffCode: entity.tariffCode,
                                    forworderCode: entity.forworderCode,
                                    copyToNewTariffItemTierList: TariffItemTierList.get(entity.tariffType + entity.tariffCode),
                                } as StandardProposalItemPreviewEntity));

                                previewEntities.set(companyCode, companyItems);
                            });
                            acc.push({ companyCode, proposalNo: '', copyToNewTariff: previewEntities.get(companyCode) ?? [] });
                        }
                        return acc;
                    }, filterCompanyCode);

                }
                return {
                    ...prevState,
                    [fieldKey]: val ?? [],
                    isEditProposalNo: false,
                    copyToProposalList: updatedCompanyCode,
                }
            })
        } else {
            proposalCopyingForRecalDispatch(prevState => {
                let updatedCompanyCode: StdProposalCopyToNewProposalEntity[] = [];
                if (val) {
                    let copyToCompanyList = val as string[];
                    let items: StdProposalCopyToNewProposalEntity[] = prevState.copyToNewProposalList?.slice();
                    let tariffItems = prevState.selectedItemRows?.slice();
                    const filterCompanyCode = items.filter(item => copyToCompanyList.includes(item.companyCode));
                    updatedCompanyCode = copyToCompanyList.reduce((acc, companyCode) => {
                        const existingCode = acc?.find(item => item.companyCode === companyCode);
                        if (!existingCode) {
                            let TariffItemTierList = new Map<string, StdProposalTariffItemTierEntity[]>();
                            tariffItems.forEach(tariff => {
                                const tierList = tariff.tierList?.slice();
                                const copyToPercentages = prevState.copyToPercentageList.filter(item => item.tariffType === tariff.tariffType);
                                let copyToNewTariffItemTierList: StdProposalTariffItemTierEntity[] = [];
                                if (!_.isEmpty(copyToPercentages)) {
                                    const calPercentage = copyToPercentages[0];
                                    copyToNewTariffItemTierList = tierList ? tierList.map(tier => {
                                        const newRate = applyRounding(
                                            (tier?.rate ?? 0) * (calPercentage.chargePercentage * 0.01),
                                            calPercentage.roundMethod,
                                            calPercentage.decimalPlace
                                        );
                                        const newMinimum = applyRounding(
                                            (tier?.minimum ?? 0) * (calPercentage.chargePercentage * 0.01),
                                            calPercentage.roundMethod,
                                            calPercentage.decimalPlace
                                        );
                                        const newSurcharge = tier?.surcharge ? applyRounding(
                                            tier?.surcharge * (calPercentage.chargePercentage * 0.01),
                                            calPercentage.roundMethod,
                                            calPercentage.decimalPlace
                                        ) : null;
                                        const newAdditionalRate = tier?.additionalRate ? applyRounding(
                                            tier?.additionalRate * (calPercentage.chargePercentage * 0.01),
                                            calPercentage.roundMethod,
                                            calPercentage.decimalPlace
                                        ) : null;
                                        return {
                                            ...tier,
                                            rate: newRate,
                                            minimum: newMinimum,
                                            surcharge: newSurcharge,
                                            additionalRate: newAdditionalRate,
                                        }
                                    }) : [];
                                }
                                TariffItemTierList.set(tariff.tariffType + tariff.tariffCode, copyToNewTariffItemTierList);
                            });
                            // let previewEntities: StandardProposalItemPreviewEntity[] = [];
                            // previewEntities = copyToCompanyList.flatMap(companyCode =>
                            //     tariffItems.map(entity => ({
                            //         companyCode,
                            //         itemId: entity.key,
                            //         tariffType: entity.tariffType,
                            //         tariffCode: entity.tariffCode,
                            //         forworderCode: entity.forworderCode,
                            //         copyToNewTariffItemTierList: copyToNewTariffItemTierList
                            //     } as StandardProposalItemPreviewEntity))
                            // );
                            let previewEntities = new Map<string, StandardProposalItemPreviewEntity[]>();
                            copyToCompanyList.forEach(companyCode => {
                                const companyItems = tariffItems.map(entity => ({
                                    companyCode,
                                    itemId: entity.key,
                                    tariffType: entity.tariffType,
                                    tariffCode: entity.tariffCode,
                                    forworderCode: entity.forworderCode,
                                    copyToNewTariffItemTierList: TariffItemTierList.get(entity.tariffType + entity.tariffCode),
                                } as StandardProposalItemPreviewEntity));

                                previewEntities.set(companyCode, companyItems);
                            });
                            let newEty = { ...EMPTY_STD_PROPOSAL_COPY_TO_NEW_PROPOSAL_ENTITY };
                            newEty = {
                                ...newEty,
                                companyCode: companyCode,
                                copyToNewTariff: previewEntities.get(companyCode) ?? [],
                            }
                            acc.push(newEty);
                        }
                        return acc;
                    }, filterCompanyCode);

                }
                return {
                    ...prevState,
                    [fieldKey]: val ?? [],
                    isEditProposalNo: false,
                    copyToNewProposalList: updatedCompanyCode,
                }
            })
        }
    }

    const onDateRangeChange = (startDate: any, endDate: any, dateFields: { startField: string, endField: string }) => {
        proposalCopyingForRecalDispatch(prevState => ({
            ...prevState,
            searchCriteria: {
                ...prevState.searchCriteria,
                [dateFields.startField]: startDate,
                [dateFields.endField]: endDate,
            }
        }))
    }

    const onShowLoading = () => {
        proposalCopyingForRecalDispatch(prevState => {
            return {
                ...prevState,
                isLoading: true,
            }
        })
    }

    const onHideLoading = () => {
        proposalCopyingForRecalDispatch(prevState => {
            return {
                ...prevState,
                isLoading: false,
            }
        })
    }

    const onCloseCopyToCompanyPanel = (isSave: boolean) => {
        proposalCopyingForRecalDispatch(prevState => {
            return {
                ...prevState,
                companyCodeList: isSave ? prevState.companyCodeList : [],
                isShowCopyToCompanyPanel: false,
            }
        })
    }

    const onCopyToCompanyCloseClick = () => {
        proposalCopyingForRecalDispatch(prevState => {
            return {
                ...prevState,
                isShowCopyToCompanyPanel: false,
                companyCodeList: [],
                copyToProposalList: [],
                copyToNewProposalList: [],
                copyToPercentageList: [],
            }
        })
    }

    const onCopyToCompanyOpenClick = async (isNew: boolean) => {
        proposalCopyingForRecalDispatch(prevState => {
            const rows = prevState.selectedItemRows.slice();
            let copyToEntities: StdProposalCopyToEntity[] = [];
            let items: ProposalCopyingForRecalItemEntity[] = rows?.slice();
            let uniqueTariffTypes = new Set<string>();
            if (items.length > 0) {
                let uniqueItems = items.filter(item => {
                    if (!uniqueTariffTypes.has(item.tariffType)) {
                        uniqueTariffTypes.add(item.tariffType);
                        return true;
                    }
                    return false;
                });
                copyToEntities = uniqueItems.map((tariff) => ({
                    tariffType: tariff.tariffType,
                    chargePercentage: 100,
                    roundMethod: "ROUND",
                    decimalPlace: "2",
                }));
            }
            return {
                ...prevState,
                isCopyToNewProposal: isNew,
                isShowCopyToCompanyPanel: true,
                selectedItemRows: rows,
                copyToPercentageList: copyToEntities,
            }
        })
    }

    const updateSelectedPercentageRow = async (rows: any[]) => {
        if (rows.length === 1) {
            proposalCopyingForRecalDispatch(prevState => {
                return {
                    ...prevState,
                    copyToPercentage: rows[0],
                }
            })

        } else {
            proposalCopyingForRecalDispatch(prevState => {
                return {
                    ...prevState,
                    isEditPercentage: false,
                }
            })
        }
    }

    const updateSelectedNewProposalNoRow = async (rows: any[]) => {
        if (rows.length === 1) {
            proposalCopyingForRecalDispatch(prevState => {
                return {
                    ...prevState,
                    copyToEditNewProposal: rows[0],
                }
            })
        } else {
            proposalCopyingForRecalDispatch(prevState => {
                return {
                    ...prevState,
                    isEditNewProposalNo: false,
                }
            })
        }
    }

    const updateSelectedProposalNoRow = async (rows: any[]) => {
        if (rows.length === 1) {
            proposalCopyingForRecalDispatch(prevState => {
                return {
                    ...prevState,
                    copyToProposalNo: rows[0],
                }
            })
        } else {
            proposalCopyingForRecalDispatch(prevState => {
                return {
                    ...prevState,
                    isEditProposalNo: false,
                }
            })
        }
    }

    const onPercentageDoubleClick = async (entity: StdProposalCopyToEntity) => {
        return proposalCopyingForRecalDispatch(prevState => {
            return {
                ...prevState,
                copyToEditPercentage: entity,
                isEditPercentage: true,
                isEditProposalNo: false,
                masterState: {
                    ...prevState.masterState,
                    allFormState: {},
                }

            }
        });
    }

    const onProposalNoDoubleClick = async (entity: StdProposalCopyToProposalEntity) => {
        let searchCriteria = { ...EMPTY_PROPOSAL_COPY_FOR_RECAL_SEARCH_CRITERIA };
        searchCriteria =
        {
            ...searchCriteria,
            companyCode: entity.companyCode,

        }
        const data = await proposalCopyingForRecalRepo.searchRecalProposal(searchCriteria);
        if (data) {
            let companyCodeProposalNoDropdownOption = data?.map((ety) => ({
                dropdownLabel: ety.proposalNo ?? "",
                tagLabel: ety.proposalNo ?? "",
                value: ety.proposalNo ?? "",
            })) ?? []

            proposalCopyingForRecalDispatch(prevState => {
                return {
                    ...prevState,
                    copyToEditProposalNo: entity,
                    isEditProposalNo: true,
                    isEditPercentage: false,
                    dynamicOptions: {
                        ...prevState.dynamicOptions,
                        companyCodeProposalNoDropdownOptions: companyCodeProposalNoDropdownOption,
                    },
                    masterState: {
                        ...prevState.masterState,
                        allFormState: {},
                    }
                }
            })
        }
    }

    const onNewProposalNoDoubleClick = async (entity: StdProposalCopyToNewProposalEntity) => {
        proposalCopyingForRecalDispatch(prevState => {
            return {
                ...prevState,
                copyToEditNewProposal: entity,
                isEditProposalNo: false,
                isEditPercentage: false,
                isEditNewProposalNo: true,
                masterState: {
                    ...prevState.masterState,
                    allFormState: {},
                }
            }
        })
    }

    const onCopyPercentageFieldChange = async (fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) => {
        let val: any = fieldValue;
        if (_.isArray(val)) {
            val = _.uniq(val?.map((item: any) => item.value || item.key));
        }

        proposalCopyingForRecalDispatch(prevState => {
            if (fieldKey === 'chargePercentage') {
                var posIntReg = /^\d*(\.\d{0,2})?$/;
                if (!posIntReg.test(val)) {
                    val = prevState.copyToEditPercentage[fieldKey];
                }
            }
            return {
                ...prevState,
                copyToEditPercentage: {
                    ...prevState.copyToEditPercentage,
                    [fieldKey]: val,
                },
                masterState: {
                    ...prevState.masterState,
                    allFormState: {
                        ...prevState.masterState.allFormState,
                        [fieldKey]: ''
                    },
                }
            }
        })
    }

    const onCopyNewProposalNoFieldChange = async (fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) => {
        let val: any = fieldValue;
        if (_.isArray(val)) {
            val = _.uniq(val?.map((item: any) => item.value || item.key));
        }

        proposalCopyingForRecalDispatch(prevState => {
            return {
                ...prevState,
                copyToEditNewProposal: {
                    ...prevState.copyToEditNewProposal,
                    [fieldKey]: val,
                },
                masterState: {
                    ...prevState.masterState,
                    allFormState: {
                        ...prevState.masterState.allFormState,
                        [fieldKey]: ''
                    },
                }
            }
        })
    }

    const onCopyProposalNoFieldChange = async (fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) => {
        let val: any = fieldValue;
        if (_.isArray(val)) {
            val = _.uniq(val?.map((item: any) => item.value || item.key));
        }

        proposalCopyingForRecalDispatch(prevState => {
            return {
                ...prevState,
                copyToEditProposalNo: {
                    ...prevState.copyToEditProposalNo,
                    [fieldKey]: val,
                },
                masterState: {
                    ...prevState.masterState,
                    allFormState: {
                        ...prevState.masterState.allFormState,
                        [fieldKey]: ''
                    },
                }
            }
        })
    }

    const onTariffItemCopyEditCloseClick = async () => {
        proposalCopyingForRecalDispatch(prevState => {
            return {
                ...prevState,
                isEditPercentage: false,
                isEditProposalNo: false,
                isEditNewProposalNo: false,
                copyToEditPercentage: { ...EMPTY_STD_PROPOSAL_COPY_TO_ENTITY },
                copyToProposalNo: { ...EMPTY_STD_PROPOSAL_COPY_TO_PROPOSAL_ENTITY },
            }
        })
    }

    const onCopyEditPercentageSaveClick = async (copyToEditPercentage: StdProposalCopyToEntity) => {
        const valResult = await Validation(copyToEditPercentageValidationSchema).ValidateFormOnly(copyToEditPercentage);
        let validatedResult: { [x: string]: string } = {};
        if (valResult) {
            validatedResult = { ...valResult };

            proposalCopyingForRecalDispatch(prevState => {
                return {
                    ...prevState,
                    masterState: {
                        ...prevState.masterState,
                        allFormState: {
                            ...validatedResult
                        },
                    }
                }
            });
        } else {
            proposalCopyingForRecalDispatch(prevState => {
                const data = prevState.copyToPercentageList?.slice();
                let edit = copyToEditPercentage;
                const updatedArr = data.map(item =>
                    item.tariffType === edit.tariffType ? edit : item
                );
                const tariffItemList = prevState.selectedItemRows?.slice();
                const calPercentageList = updatedArr.filter(entity => entity.tariffType === edit.tariffType);
                const calPercentage = calPercentageList[0];
                if (prevState.isCopyToNewProposal) {
                    const newProposalList = prevState.copyToNewProposalList.slice();
                    const updateProposalList = newProposalList.map(item => {
                        return {
                            ...item,
                            copyToNewTariff: item.copyToNewTariff.map(tariff => {
                                if (tariff.tariffType === edit.tariffType) {
                                    const tarffItem = tariffItemList.find(entity => entity.tariffType === tariff.tariffType && entity.tariffCode === tariff.tariffCode);
                                    return {
                                        ...tariff,
                                        copyToNewTariffItemTierList: tarffItem && tarffItem.tierList ? tarffItem.tierList.map(tier => {
                                            const newRate = applyRounding(
                                                (tier?.rate ?? 0) * (calPercentage.chargePercentage * 0.01),
                                                calPercentage.roundMethod,
                                                calPercentage.decimalPlace
                                            );
                                            const newMinimum = applyRounding(
                                                (tier?.minimum ?? 0) * (calPercentage.chargePercentage * 0.01),
                                                calPercentage.roundMethod,
                                                calPercentage.decimalPlace
                                            );
                                            const newSurcharge = tier?.surcharge ? applyRounding(
                                                tier?.surcharge * (calPercentage.chargePercentage * 0.01),
                                                calPercentage.roundMethod,
                                                calPercentage.decimalPlace
                                            ) : null;
                                            const newAdditionalRate = tier?.additionalRate ? applyRounding(
                                                tier?.additionalRate * (calPercentage.chargePercentage * 0.01),
                                                calPercentage.roundMethod,
                                                calPercentage.decimalPlace
                                            ) : null;
                                            return {
                                                ...tier,
                                                rate: newRate,
                                                minimum: newMinimum,
                                                surcharge: newSurcharge,
                                                additionalRate: newAdditionalRate,
                                            }
                                        }) : []
                                    }
                                }
                                return tariff;
                            })
                        }
                    }
                    );
                    return {
                        ...prevState,
                        copyToPercentageList: updatedArr,
                        copyToNewProposalList: updateProposalList,
                        isEditPercentage: false,
                    }
                } else {
                    const newProposalList = prevState.copyToProposalList.slice();
                    const updateProposalList = newProposalList.map(item => {
                        return {
                            ...item,
                            copyToNewTariff: item.copyToNewTariff.map(tariff => {
                                if (tariff.tariffType === edit.tariffType) {
                                    const tarffItem = tariffItemList.find(entity => entity.tariffType === tariff.tariffType && entity.tariffCode === tariff.tariffCode);
                                    return {
                                        ...tariff,
                                        copyToNewTariffItemTierList: tarffItem && tarffItem.tierList ? tarffItem.tierList.map(tier => {
                                            const newRate = applyRounding(
                                                (tier?.rate ?? 0) * (calPercentage.chargePercentage * 0.01),
                                                calPercentage.roundMethod,
                                                calPercentage.decimalPlace
                                            );
                                            const newMinimum = applyRounding(
                                                (tier?.minimum ?? 0) * (calPercentage.chargePercentage * 0.01),
                                                calPercentage.roundMethod,
                                                calPercentage.decimalPlace
                                            );
                                            const newSurcharge = tier?.surcharge ? applyRounding(
                                                tier?.surcharge * (calPercentage.chargePercentage * 0.01),
                                                calPercentage.roundMethod,
                                                calPercentage.decimalPlace
                                            ) : null;
                                            const newAdditionalRate = tier?.additionalRate ? applyRounding(
                                                tier?.additionalRate * (calPercentage.chargePercentage * 0.01),
                                                calPercentage.roundMethod,
                                                calPercentage.decimalPlace
                                            ) : null;
                                            return {
                                                ...tier,
                                                rate: newRate,
                                                minimum: newMinimum,
                                                surcharge: newSurcharge,
                                                additionalRate: newAdditionalRate,
                                            }
                                        }) : []
                                    }
                                }
                                return tariff;
                            })
                        }
                    }
                    );
                    return {
                        ...prevState,
                        copyToPercentageList: updatedArr,
                        copyToProposalList: updateProposalList,
                        isEditPercentage: false,
                    }
                }
            })
        }
    }

    const onCopyEditProposalNoSaveClick = async (copyToEditProposalNo: StdProposalCopyToProposalEntity) => {
        const valResult = await Validation(copyEditProposalNoValidationSchema).ValidateFormOnly(copyToEditProposalNo);
        let validatedResult: { [x: string]: string } = {};
        if (valResult) {
            validatedResult = { ...valResult };

            proposalCopyingForRecalDispatch(prevState => {
                return {
                    ...prevState,
                    masterState: {
                        ...prevState.masterState,
                        allFormState: {
                            ...validatedResult
                        },
                    }
                }
            });
        } else {
            proposalCopyingForRecalDispatch(prevState => {
                const data = prevState.copyToProposalList?.slice();
                let edit = copyToEditProposalNo;
                const updatedArr = data.map(item =>
                    item.companyCode === edit.companyCode ? edit : item
                );
                return {
                    ...prevState,
                    copyToProposalList: updatedArr,
                    isEditProposalNo: false,
                }
            })
        }
    }

    const onCopyEditNewProposalNoSaveClick = async (copyToEditNewProposal: StdProposalCopyToNewProposalEntity) => {
        const valResult = await Validation(copyEditNewProposalNoValidationSchema).ValidateFormOnly(copyToEditNewProposal);
        let validatedResult: { [x: string]: string } = {};
        if (valResult) {
            validatedResult = { ...valResult };

            proposalCopyingForRecalDispatch(prevState => {
                return {
                    ...prevState,
                    masterState: {
                        ...prevState.masterState,
                        allFormState: {
                            ...validatedResult
                        },
                    }
                }
            });
        } else {
            proposalCopyingForRecalDispatch(prevState => {
                const data = prevState.copyToNewProposalList?.slice();
                let edit = copyToEditNewProposal;
                const updatedArr = data.map(item =>
                    item.companyCode === edit.companyCode ? edit : item
                );
                return {
                    ...prevState,
                    copyToNewProposalList: updatedArr,
                    isEditNewProposalNo: false,
                }
            })
        }
    }

    const onTariffItemNewCopy = async (fromTariffItemList: ProposalCopyingForRecalItemEntity[], copyToPercentageList: StdProposalCopyToEntity[], copyToNewProposalList: StdProposalCopyToNewProposalEntity[], currenctProposal: ProposalCopyingForRecalEntity) => {
        const criteria: CopyTariffProposalItemForRecalCriteria = {
            tariffCodeIds: [],
            fromTariffItemList: fromTariffItemList,
            copyToProposalList: [],
            copyToNewProposalList: copyToNewProposalList,
            copyToPercentageList: copyToPercentageList,
            currenctProposal: currenctProposal
        }
        return proposalCopyingForRecalRepo.copyProposalTariffItemsForRecal(criteria);
    }

    const onTariffItemCombineCopy = async (fromTariffItemList: ProposalCopyingForRecalItemEntity[], copyToPercentageList: StdProposalCopyToEntity[], copyToProposalList: StdProposalCopyToProposalEntity[], currenctProposal: ProposalCopyingForRecalEntity) => {
        const criteria: CopyTariffProposalItemForRecalCriteria = {
            tariffCodeIds: [],
            copyToNewProposalList: [],
            fromTariffItemList: fromTariffItemList,
            copyToProposalList: copyToProposalList,
            copyToPercentageList: copyToPercentageList,
            currenctProposal: currenctProposal
        }
        return proposalCopyingForRecalRepo.copyProposalTariffItemsForRecal(criteria);
    }


    const searchNewProposalsForRecal = async () => {
        await proposalCopyingForRecalRepo.searchRecalProposal(EMPTY_PROPOSAL_COPY_FOR_RECAL_SEARCH_CRITERIA).then((data) => {
            if (data) {
                proposalCopyingForRecalDispatch(prevState => {
                    return {
                        ...prevState,
                        selectedRows: [],
                        isForRecalCount: data.length
                    }
                })
            }
        }).catch((error) => {
            return [];
        })

    }

    const onRecalItemDoubleClick = async (entity: ProposalCopyingForRecalItemEntity, isForRecal: boolean) => {
        proposalCopyingForRecalDispatch(prevState => {
            return {
                ...prevState,
                isShowEditPanel: isForRecal,
                isShowTariffItemTierPanel: !isForRecal,
                currentItemRow: entity,
                itemTierList: entity.tierList ?? []
            }
        })

    }

    const updateSelectedItemTierRows = async (rows: any[]) => {
        proposalCopyingForRecalDispatch(prevState => {
            return {
                ...prevState,
                selectedItemTierRows: rows
            }
        })

    }

    const onItemTierCloseClick = async () => {
        proposalCopyingForRecalDispatch(prevState => {
            return {
                ...prevState,
                isShowEditPanel: false,
                isShowTariffItemTierPanel: false,
                isEdit: false,
            }
        })

    }

    const onItemTierEditClick = async () => {
        proposalCopyingForRecalDispatch(prevState => {
            return {
                ...prevState,
                isEdit: true,
            }
        })

    }

    const onRecalItemTierDoubleClick = async (entity: StdProposalTariffItemTierEntity) => {
        proposalCopyingForRecalDispatch(prevState => {
            return {
                ...prevState,
                isShowEditTierPanel: true,
                currentItemTier: entity,
                currentEditItemTier: entity,
            }
        })

    }

    const onItemTierDeleteClick = async (rows: StdProposalTariffItemTierEntity[]) => {
        proposalCopyingForRecalDispatch(prevState => {
            return {
                ...prevState,
            }
        })

    }


    const onTierFieldChange = async (fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) => {
        let val: any = fieldValue;

        proposalCopyingForRecalDispatch(prevState => {
            return {
                ...prevState,
                currentEditItemTier: {
                    ...prevState.currentEditItemTier,
                    [fieldKey]: val,
                },
                masterState: {
                    ...prevState.masterState,
                    allFormState: {
                        ...prevState.masterState.allFormState,
                        [fieldKey]: ''
                    },
                }
            }
        })
    }

    const cancelEdit = async () => {

        proposalCopyingForRecalDispatch(prevState => {
            return {
                ...prevState,
                isShowCopyEditTierPanel: false,
                currentEditItemTier: { ...EMPTY_STD_PROPOSAL_TARIFF_ITEM_TIER_ENTITY },
                masterState: {
                    ...prevState.masterState,
                    allFormState: {}
                }
            }
        })
    }

    const onSaveClicked = () => {
        proposalCopyingForRecalDispatch(prevState => {
            return {
                ...prevState,
                masterState: {
                    ...prevState.masterState,
                    isSaveClicked: true,
                    allFormState: {},
                }
            }
        })
    }

    const onTempSaveItemTier = async (currentEditItemTier: StdProposalTariffItemTierEntity) => {
        const valResult = await Validation(createTempSaveTariffItemTierValidationSchema).ValidateFormOnly(currentEditItemTier);
        let validatedResult: { [x: string]: string } = {};
        if (valResult) {
            validatedResult = { ...valResult };

            proposalCopyingForRecalDispatch(prevState => {
                return {
                    ...prevState,
                    masterState: {
                        ...prevState.masterState,
                        allFormState: {
                            ...validatedResult
                        },
                    }
                }
            });
        } else {
            proposalCopyingForRecalDispatch(prevState => {
                let newTiers: StdProposalTariffItemTierEntity[] = [];
                let tier = currentEditItemTier;
                if (prevState.itemTierList) {
                    newTiers = prevState.itemTierList?.slice();
                    newTiers = newTiers.map(entity => entity.tierNo === tier.tierNo ? tier : entity);
                } else {
                    tier = {
                        ...tier,
                        tierNo: newTiers.length + 1,
                        newRecord: true,
                    }
                    newTiers.push(tier);
                }
                return {
                    ...prevState,
                    itemTierList: newTiers,
                    isShowEditTierPanel: false,
                    currentItemRow: {
                        ...prevState.currentItemRow,
                        tierList: newTiers
                    }
                }
            })
        }
    }
    const onTempSaveCopyItemTier = async (currentEditItemTier: StdProposalTariffItemTierEntity) => {
        const valResult = await Validation(createTempSaveTariffItemTierValidationSchema).ValidateFormOnly(currentEditItemTier);
        let validatedResult: { [x: string]: string } = {};
        if (valResult) {
            validatedResult = { ...valResult };

            proposalCopyingForRecalDispatch(prevState => {
                return {
                    ...prevState,
                    masterState: {
                        ...prevState.masterState,
                        allFormState: {
                            ...validatedResult
                        },
                    }
                }
            });
        } else {
            proposalCopyingForRecalDispatch(prevState => {
                let newTiers: StdProposalTariffItemTierEntity[] = [];
                let tier = currentEditItemTier;
                const itemTierList = prevState.selectCopyToNewTariff.copyToNewTariffItemTierList.slice();
                if (itemTierList) {
                    newTiers = itemTierList?.slice();
                    newTiers = newTiers.map(entity => entity.tierNo === tier.tierNo ? tier : entity);
                } else {
                    tier = {
                        ...tier,
                        tierNo: newTiers.length + 1,
                        newRecord: true,
                    }
                    newTiers.push(tier);
                }

                let selectCopyToNewTariff = prevState.selectCopyToNewTariff;
                selectCopyToNewTariff = {
                    ...prevState.selectCopyToNewTariff,
                    copyToNewTariffItemTierList: newTiers
                }
                if (prevState.isCopyToNewProposal) {
                    let newProposalTariffs = prevState.copyToEditNewProposal.copyToNewTariff;
                    let newProposals = prevState.copyToNewProposalList;
                    newProposalTariffs = newProposalTariffs.map(entity => entity.tariffCode === selectCopyToNewTariff.tariffCode ? selectCopyToNewTariff : entity);
                    let newProposal = prevState.copyToEditNewProposal;
                    newProposal = {
                        ...newProposal,
                        copyToNewTariff: newProposalTariffs
                    }
                    newProposals = newProposals.map(entity => entity.companyCode === newProposal.companyCode ? newProposal : entity);
                    return {
                        ...prevState,
                        copyToNewProposalList: newProposals,
                        copyToEditNewProposal: newProposal,
                        selectCopyToNewTariff: selectCopyToNewTariff,
                        isShowCopyEditTierPanel: false,
                        isEditTier: true,
                    }
                } else {
                    const includPewviewUptProposalTariffList = prevState.copyToProposalList.flatMap(
                        entity => entity.copyToNewTariff?.map(item => ({ ...item })) ?? []
                    );
                    const preUptProposlaTariffsMap = new Map(
                        includPewviewUptProposalTariffList.map(item => [item.tariffCode, item])
                    );
                    let newProposalTariffs = prevState.copyToEditProposalNo.copyToNewTariff;
                    newProposalTariffs = prevState.copyToEditProposalNo.copyToNewTariff.map(item => {
                        const updatedEntity = preUptProposlaTariffsMap.get(item.tariffCode);
                        return updatedEntity ?? item;
                    });

                    let newProposals = prevState.copyToProposalList;
                    newProposalTariffs = newProposalTariffs.map(entity => entity.tariffCode === selectCopyToNewTariff.tariffCode ? selectCopyToNewTariff : entity);
                    let newProposal = prevState.copyToEditProposalNo;
                    newProposal = {
                        ...newProposal,
                        copyToNewTariff: newProposalTariffs
                    }
                    newProposals = newProposals.map(entity => entity.companyCode === newProposal.companyCode ? newProposal : entity);
                    return {
                        ...prevState,
                        copyToProposalList: newProposals,
                        copyToProposalNo: newProposal,
                        selectCopyToNewTariff: selectCopyToNewTariff,
                        isShowCopyEditTierPanel: false,
                        isEditTier: true,
                    }

                }
            })
        }
    }

    const onDeleteItemsClick = async (items: ProposalCopyingForRecalItemEntity[]) => {
        return proposalCopyingForRecalRepo.deleteItems(items);
    }

    const onItemTierSave = async (item: ProposalCopyingForRecalItemEntity) => {
        return proposalCopyingForRecalRepo.saveItem(item);
    }

    const onShowPreviewPanel = (isCopyToNewProposal: boolean) => {
        proposalCopyingForRecalDispatch(prevState => {

            // const companyList = isCopyToNewProposal ? prevState.copyToNewProposalList?.slice() : prevState.copyToProposalList.slice();
            // const copyToCompanyList = companyList.map(entity => entity.companyCode);
            // let previewEntities: StandardProposalItemPreviewEntity[] = [];
            // let tariffItems = prevState.selectedItemRows?.slice();
            // previewEntities = copyToCompanyList.flatMap(companyCode =>
            //     tariffItems.map(entity => ({
            //         companyCode,
            //         itemId: entity.key,
            //         tariffType: entity.tariffType,
            //         tariffCode: entity.tariffCode,
            //         forworderCode: entity.forworderCode
            //     } as StandardProposalItemPreviewEntity))
            // );

            return {
                ...prevState,
                isShowPreviewPanel: true,
                isEditTier: prevState.isEditTier ? prevState.isEditTier : false,
                // copyToNewTariffList: previewEntities
            }
        });
    }
    const onClosePreviewPanel = () => {
        proposalCopyingForRecalDispatch(prevState => {
            return {
                ...prevState,
                isShowPreviewPanel: false,
                selectCopyToNewTariff: { ...EMPTY_STANDARD_PROPOSAL_ITEM_PREVIEW_ENTITY },
            }
        });
    }

    const onDoubleClickTariffItemTierRatePanel = (entity: StdProposalTariffItemTierEntity) => {
        proposalCopyingForRecalDispatch(prevState => {
            return {
                ...prevState,
                isShowCopyEditTierPanel: true,
                currentEditItemTier: entity,
            }
        });
    }

    const onShowTariffItemTierRatePanel = (entityList: StdProposalTariffItemTierEntity[]) => {
        if (!_.isEmpty(entityList)) {
            const entity = entityList[0];
            proposalCopyingForRecalDispatch(prevState => {
                return {
                    ...prevState,
                    currentItemTier: entity,
                }
            });
        } else {
            proposalCopyingForRecalDispatch(prevState => {
                return {
                    ...prevState,
                    currentItemTier: { ...EMPTY_STD_PROPOSAL_TARIFF_ITEM_TIER_ENTITY },
                }
            });
        }
    }
    const onShowTariffItemTierPanel = (entityList: StandardProposalItemPreviewEntity[], isEditTier: boolean) => {
        if (isEditTier) {
            if (!_.isEmpty(entityList)) {
                proposalCopyingForRecalDispatch(prevState => {
                    return {
                        ...prevState,
                        selectCopyToNewTariff: entityList[0],
                    }
                });
            } else {
                proposalCopyingForRecalDispatch(prevState => {
                    return {
                        ...prevState,
                        selectCopyToNewTariff: { ...EMPTY_STANDARD_PROPOSAL_ITEM_PREVIEW_ENTITY },
                    }
                });
            }
        } else {
            if (!_.isEmpty(entityList)) {
                const entity = entityList[0];
                proposalCopyingForRecalDispatch(prevState => {
                    let copyToNewTariffItemTierList: StdProposalTariffItemTierEntity[] = [];
                    let tariffItems = prevState.selectedItemRows?.slice();
                    let copyToPercentageList = prevState.copyToPercentageList?.slice();
                    tariffItems = tariffItems?.filter(tariffItem => tariffItem.key === entity.itemId);
                    if (!_.isEmpty(tariffItems)) {
                        const tierList = tariffItems[0].tierList?.slice();
                        const copyToPercentages = copyToPercentageList.filter(item => item.tariffType === entity.tariffType);
                        if (!_.isEmpty(copyToPercentages)) {
                            const calPercentage = copyToPercentages[0];
                            copyToNewTariffItemTierList = tierList ? tierList.map(tier => {
                                const newRate = applyRounding(
                                    (tier?.rate ?? 0) * (calPercentage.chargePercentage * 0.01),
                                    calPercentage.roundMethod,
                                    calPercentage.decimalPlace
                                );
                                const newMinimum = applyRounding(
                                    (tier?.minimum ?? 0) * (calPercentage.chargePercentage * 0.01),
                                    calPercentage.roundMethod,
                                    calPercentage.decimalPlace
                                );
                                const newSurcharge = tier?.surcharge ? applyRounding(
                                    tier?.surcharge * (calPercentage.chargePercentage * 0.01),
                                    calPercentage.roundMethod,
                                    calPercentage.decimalPlace
                                ) : null;
                                const newAdditionalRate = tier?.additionalRate ? applyRounding(
                                    tier?.additionalRate * (calPercentage.chargePercentage * 0.01),
                                    calPercentage.roundMethod,
                                    calPercentage.decimalPlace
                                ) : null;
                                return {
                                    ...tier,
                                    rate: newRate,
                                    minimum: newMinimum,
                                    surcharge: newSurcharge,
                                    additionalRate: newAdditionalRate,
                                }
                            }) : [];
                        }
                    }
                    return {
                        ...prevState,
                        selectCopyToNewTariff: {
                            ...entity,
                            copyToNewTariffItemTierList: copyToNewTariffItemTierList
                        },
                    }
                });
            } else {
                proposalCopyingForRecalDispatch(prevState => {
                    return {
                        ...prevState,
                        selectCopyToNewTariff: { ...EMPTY_STANDARD_PROPOSAL_ITEM_PREVIEW_ENTITY },
                    }
                });
            }
        }
    }

    const applyRounding = (value: number, method: string, decimalPlace: string) => {
        const factor = Math.pow(10, Number(decimalPlace));
        switch (method) {
            case 'ROUND':
                return Math.round(value * factor) / factor;
            case 'ROUND DOWN':
                return Math.floor(value * factor) / factor;
            case 'ROUND UP':
                return Math.ceil(value * factor) / factor;
            default:
                throw new Error(`Unknown rounding method: ${method}`);
        }
    }

    return {
        onTempSaveCopyItemTier: onTempSaveCopyItemTier,
        onDoubleClickTariffItemTierRatePanel: onDoubleClickTariffItemTierRatePanel,
        onShowTariffItemTierRatePanel: onShowTariffItemTierRatePanel,
        onShowTariffItemTierPanel: onShowTariffItemTierPanel,
        onShowPreviewPanel: onShowPreviewPanel,
        onClosePreviewPanel: onClosePreviewPanel,
        onItemTierSave: onItemTierSave,
        onItemTierEditClick: onItemTierEditClick,
        onDeleteItemsClick: onDeleteItemsClick,
        searchStandardProposalItem: searchStandardProposalItem,
        onTempSaveItemTier: onTempSaveItemTier,
        cancelEdit: cancelEdit,
        onTierFieldChange: onTierFieldChange,
        onItemTierDeleteClick: onItemTierDeleteClick,
        onRecalItemTierDoubleClick: onRecalItemTierDoubleClick,
        onItemTierCloseClick: onItemTierCloseClick,
        updateSelectedItemTierRows: updateSelectedItemTierRows,
        onRecalItemDoubleClick: onRecalItemDoubleClick,
        searchNewProposalsForRecal: searchNewProposalsForRecal,
        onTariffItemCombineCopy: onTariffItemCombineCopy,
        onTariffItemNewCopy: onTariffItemNewCopy,
        onTariffItemCopyEditCloseClick: onTariffItemCopyEditCloseClick,
        onCopyEditPercentageSaveClick: onCopyEditPercentageSaveClick,
        onCopyEditProposalNoSaveClick: onCopyEditProposalNoSaveClick,
        onCopyEditNewProposalNoSaveClick: onCopyEditNewProposalNoSaveClick,
        onCopyPercentageFieldChange: onCopyPercentageFieldChange,
        onCopyNewProposalNoFieldChange: onCopyNewProposalNoFieldChange,
        onCopyProposalNoFieldChange: onCopyProposalNoFieldChange,
        onPercentageDoubleClick: onPercentageDoubleClick,
        onProposalNoDoubleClick: onProposalNoDoubleClick,
        onNewProposalNoDoubleClick: onNewProposalNoDoubleClick,
        updateSelectedProposalNoRow: updateSelectedProposalNoRow,
        updateSelectedNewProposalNoRow: updateSelectedNewProposalNoRow,
        updateSelectedPercentageRow: updateSelectedPercentageRow,
        onShowLoading: onShowLoading,
        onHideLoading: onHideLoading,
        loadDropdownOption: loadDropdownOption,
        searchProposalsForRecal: searchProposalsForRecal,
        updateSelectedRows: updateSelectedRows,
        onGroupButtonClick: onGroupButtonClick,
        onSearchCriteriaResetClick: onSearchCriteriaResetClick,
        onSearchClick: onSearchClick,
        onFieldChange: onFieldChange,
        onDateRangeChange: onDateRangeChange,
        updateSelectedTariffItemRows: updateSelectedTariffItemRows,
        onTariffItemCloseClick: onTariffItemCloseClick,
        onCloseCopyToCompanyPanel: onCloseCopyToCompanyPanel,
        onCopyToFieldChange: onCopyToFieldChange,
        onCopyToCompanyOpenClick: onCopyToCompanyOpenClick,
        onCopyToCompanyCloseClick: onCopyToCompanyCloseClick,
        onSaveClicked: onSaveClicked,

    }
}